import { useEffect, useState } from "react";
import { CheckCircle, Users, Flag, X } from "@phosphor-icons/react";

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");
const LONG_TASK_MIN = 30;

function useNow(ms = 30000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

const minutesSince = (iso, now) => Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));

export function PendingNoteDialog({ reason, onCancel, onConfirm, busy }) {
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); onCancel(); }}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="pending-note-dialog">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="label-cap text-red-600">marcar pendiente</div>
            <h4 className="font-display font-bold text-lg">{reason}</h4>
          </div>
          <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-[var(--bg)]" data-testid="pending-note-close"><X size={18} /></button>
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={400}
          autoFocus
          placeholder="Información adicional (opcional)…"
          data-testid="pending-note-input"
          className="w-full px-3 py-2 rounded-xl border border-[var(--border)] text-sm"
        />
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={onCancel} className="flex-1 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">Cancelar</button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onConfirm(note)}
            data-testid="pending-note-confirm"
            className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-display font-bold disabled:opacity-50"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

function ActiveInfo({ name, active, now }) {
  return (
    <div className="mt-2 text-xs font-medium" data-testid={`break-reason-workers-${slug(name)}`}>
      <div className="flex items-center gap-1 opacity-90 mb-0.5"><Users size={13} weight="bold" />{active.length} en curso</div>
      {active.map((b) => (
        <div key={b.id} className="truncate" data-testid={`break-reason-timer-${b.id}`}>
          {b.user_name} · {minutesSince(b.start_time, now)} min
        </div>
      ))}
    </div>
  );
}

function PendingInfo({ name, pending }) {
  return (
    <div className="mt-2 text-xs" data-testid={`break-reason-pending-${slug(name)}`}>
      <div className="font-display font-bold uppercase tracking-wide">Pendiente</div>
      {pending.note && <div className="line-clamp-2 italic">“{pending.note}”</div>}
      {pending.marked_by && <div className="opacity-80">por {pending.marked_by}</div>}
    </div>
  );
}

function TaskCard({ r, active, pending, isSel, now, onSelect, onFlag }) {
  const isActive = active.length > 0;
  const isLong = active.some((b) => minutesSince(b.start_time, now) >= LONG_TASK_MIN);
  const color = r.color || "#94a3b8";
  const style = isActive
    ? { backgroundColor: color, borderColor: color }
    : pending ? { backgroundColor: "#dc2626", borderColor: "#dc2626" } : { borderLeftColor: color };
  const tone = isActive || pending ? "text-white shadow-md" : "bg-white border-[var(--border)] border-l-[8px] hover:shadow-md";
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onSelect(r.name)}
        data-testid={`break-reason-card-${slug(r.name)}`}
        data-active={isActive ? "true" : "false"}
        data-pending={pending ? "true" : "false"}
        data-long={isLong ? "true" : "false"}
        aria-pressed={isSel}
        style={style}
        className={`w-full min-h-[120px] rounded-2xl p-3 text-left flex flex-col justify-between border-2 transition-[transform,box-shadow] duration-150 active:scale-[0.97] ${tone} ${isLong ? "animate-pulse" : ""} ${isSel ? "ring-4 ring-offset-2 ring-slate-900 scale-[1.02]" : ""}`}
      >
        {isSel && <CheckCircle size={22} weight="fill" className="absolute top-2 right-2" data-testid="break-reason-selected-icon" />}
        <div className="font-display font-bold text-base leading-tight pr-7">{r.name}</div>
        {isActive ? <ActiveInfo name={r.name} active={active} now={now} />
          : pending ? <PendingInfo name={r.name} pending={pending} />
          : <div className="mt-2 text-[11px] uppercase tracking-wide text-[var(--text-secondary)]">{r.family_name || "Libre"}</div>}
      </button>
      {!isActive && (
        <button
          type="button"
          onClick={() => onFlag(r.name, !!pending)}
          title={pending ? "Quitar pendiente" : "Marcar pendiente"}
          data-testid={`break-reason-flag-${slug(r.name)}`}
          className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center border transition-colors ${pending ? "bg-white text-red-600 border-white" : "bg-white text-[var(--text-secondary)] border-[var(--border)] hover:text-red-600 hover:border-red-400"}`}
        >
          <Flag size={15} weight={pending ? "fill" : "bold"} />
        </button>
      )}
    </div>
  );
}

export default function TaskGrid({ reasons, openBreaks, pendingTasks = [], selected, onSelect, onFlag }) {
  const now = useNow();
  if (!reasons.length) {
    return <div className="text-sm text-[var(--text-secondary)] py-6 text-center" data-testid="break-reason-empty">(sin tareas disponibles)</div>;
  }
  const pendingMap = Object.fromEntries(pendingTasks.map((p) => [p.reason, p]));
  const ordered = [...reasons].sort((a, b) => Number(!!pendingMap[b.name]) - Number(!!pendingMap[a.name]));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="break-reason-grid">
      {ordered.map((r) => (
        <TaskCard
          key={r.name}
          r={r}
          active={openBreaks.filter((b) => b.reason === r.name)}
          pending={pendingMap[r.name]}
          isSel={selected === r.name}
          now={now}
          onSelect={onSelect}
          onFlag={onFlag}
        />
      ))}
    </div>
  );
}
